<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { NxtSpinner } from '@future-harry/nxt-styles';
import { notifyError, notifySuccess, notifyInfo } from '@/core/helpers/notification.helper';
import { useMediaLibrary } from '@/modules/media-library/composables/media-library.composable';
import type { MediaItem } from '@/modules/media-library/components/media-library.interface';
import MediaLibraryActions from './MediaLibraryActions.vue';
import MediaLibraryView from './MediaLibraryView.vue';

const props = withDefaults(
   defineProps<{
      jobBoardId: number;
      showImages?: boolean;
      showVideos?: boolean;
   }>(),
   {
      showImages: true,
      showVideos: true,
   },
);
const emit = defineEmits<{
   (e: 'onSelectMedia', mediaItem: MediaItem | null): void;
}>();

const { t } = useI18n();
const { displayedMedia, isFinished, searchValue, selectedMedia, upload, remove, edit } = useMediaLibrary(props.jobBoardId);

function isSelectable(item: MediaItem): boolean {
   return (props.showImages && item.is_image) || (props.showVideos && item.is_video);
}

function handleSelect(id: number) {
   const item = displayedMedia.value.find((img: MediaItem) => img.id === id);
   if (item && isSelectable(item)) {
      selectedMedia.value = item;
      emit('onSelectMedia', selectedMedia.value);
   }
}

async function handleFileAdded(file: File) {
   try {
      notifyInfo({ title: t('media-library.upload-started') });
      await upload(file);
      notifySuccess({ title: t('media-library.upload-success') });
   } catch (error) {
      notifyError({ title: t('media-library.upload-error') });
   }
}

async function handleDelete(id: number) {
   try {
      notifyInfo({ title: t('media-library.delete-started') });
      selectedMedia.value = null;
      await remove(id);
      notifySuccess({ title: t('media-library.delete-success') });
   } catch (error) {
      notifyError({ title: t('media-library.delete-error') });
   }
}

async function handleEdit(payload: { id: number; name: string }) {
   try {
      await edit(payload.id, { name: payload.name });
      notifySuccess({ title: t('media-library.edit-success') });
   } catch (error) {
      notifyError({ title: t('media-library.edit-error') });
   }
}
</script>

<template>
   <div v-if="!isFinished" class="loading"><NxtSpinner /></div>
   <template v-else>
      <MediaLibraryActions @on-file-added="handleFileAdded" v-model:searchValue="searchValue" />
      <MediaLibraryView
         v-if="displayedMedia.length"
         :media="displayedMedia"
         :selectedMedia="selectedMedia"
         :showImages="props.showImages"
         :showVideos="props.showVideos"
         @on-select="handleSelect"
         @on-edit="handleEdit"
         @on-delete="handleDelete"
      />
   </template>
</template>

<style lang="scss" scoped>
.loading {
   display: flex;
   justify-content: center;
   align-items: center;
   height: 100%;
}
</style>
